import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { findSuppliers, SupplierFinderResponse } from '../../services/supplierFinderService';
import { toast } from 'react-hot-toast';

interface SupplierFinderFormProps {
  onResults: (results: SupplierFinderResponse) => void;
  onLoading: (isLoading: boolean) => void;
  onError: (error: string | null) => void;
}

const SupplierFinderForm: React.FC<SupplierFinderFormProps> = ({ onResults, onLoading, onError }) => {
  const { user } = useAuth();
  const [product, setProduct] = useState('');
  const [location, setLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form validation
  const isFormValid = product.trim() !== '' && location.trim() !== '';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!isFormValid) {
      onError('Please fill in all required fields');
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check if user is logged in
    if (!user || !user.id) {
      onError('You must be logged in to use this feature');
      toast.error('You must be logged in to use this feature');
      return;
    }
    
    // Start loading
    setIsSubmitting(true);
    onLoading(true);
    onError(null);
    
    try {
      // Call API
      const results = await findSuppliers({
        product: product.trim(),
        location: location.trim(),
        userId: user.id
      });
      
      // Pass results to parent component
      onResults(results);
      toast.success('Suppliers found successfully');
    } catch (error) {
      console.error('Error finding suppliers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to find suppliers. Please try again.';
      onError(errorMessage);
      toast.error(errorMessage);
      
      // Check for subscription errors
      if (errorMessage.includes('403') || errorMessage.includes('subscription')) {
        onError('This feature requires a premium subscription');
        toast.error('This feature requires a premium subscription');
      }
    } finally {
      // End loading
      setIsSubmitting(false);
      onLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Find Trade Suppliers</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="product" className="block text-sm font-medium text-gray-700 mb-1">
            Product or Service <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="product"
            placeholder="e.g., Licensed rock band t-shirts"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            required
          />
          <p className="mt-1 text-sm text-gray-500">Specify the product or service you're looking for</p>
        </div>
        
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
            Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="location"
            placeholder="e.g., United Kingdom"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
          />
          <p className="mt-1 text-sm text-gray-500">Enter country, region, or city</p>
        </div>
        
        <div className="pt-2">
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={`w-full px-4 py-2 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isFormValid && !isSubmitting ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin inline-block mr-2">⋯</span>
                Searching...
              </>
            ) : (
              'Find Suppliers'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierFinderForm;
